import { oc } from "@orpc/contract";
import { z } from "zod";
import {
  EXTERNAL_API_AUTH_TYPES,
  EXTERNAL_API_PARAM_LOCATIONS,
  EXTERNAL_API_PARAM_TYPES,
  EXTERNAL_API_RESPONSE_TYPES,
  EXTERNAL_HTTP_METHODS,
  externalApiAuthConfigSchema,
  externalApiParamSchema,
  externalApiResponseFieldSchema,
  externalApiRetryConfigSchema,
} from "@erp/workflows";

/**
 * Public-safe DTO for an `ExternalApiDefinition` row. Auth credentials are
 * never returned in plaintext; the `authConfig` field on responses is a
 * redacted shape so the frontend can render the right form controls
 * without ever seeing the secret.
 */
const ExternalApiDefinitionDTOSchema = z.object({
  id: z.uuid(),
  organizationId: z.uuid(),
  name: z.string(),
  description: z.string().nullable(),
  isActive: z.boolean(),
  method: z.enum(EXTERNAL_HTTP_METHODS),
  baseUrl: z.string(),
  pathTemplate: z.string(),
  paramSchema: z.array(externalApiParamSchema),
  responseSchema: z.array(externalApiResponseFieldSchema),
  responseBodySchemaDefinitionId: z.string().uuid().nullable(),
  responseBodySchemaDefinitionVersionId: z.string().uuid().nullable(),
  requestBodySchemaDefinitionId: z.string().uuid().nullable(),
  requestBodySchemaDefinitionVersionId: z.string().uuid().nullable(),
  authType: z.enum(EXTERNAL_API_AUTH_TYPES),
  /** Redacted shape — never the real credential material. */
  authConfig: externalApiAuthConfigSchema,
  timeoutMs: z.number().int(),
  retryConfig: externalApiRetryConfigSchema,
  version: z.number().int(),
  lastTestedAt: z.string().nullable(),
  lastTestStatus: z.enum(["success", "failure"]).nullable(),
  lastTestError: z.string().nullable(),
  createdAt: z.string(),
  updatedAt: z.string(),
  createdBy: z.object({ id: z.string(), name: z.string() }).nullable(),
  updatedBy: z.object({ id: z.string(), name: z.string() }).nullable(),
});

export type ExternalApiDefinitionDTO = z.infer<
  typeof ExternalApiDefinitionDTOSchema
>;

const ListExternalApiDefinitionsRequestSchema = z.object({
  includeInactive: z.boolean().optional(),
  search: z.string().optional(),
});

const ListExternalApiDefinitionsResponseSchema = z.object({
  apiDefinitions: z.array(ExternalApiDefinitionDTOSchema),
});

const GetExternalApiDefinitionRequestSchema = z.object({
  id: z.uuid(),
});

/**
 * Create / update input. `authConfig` may include real credentials (caller
 * is sending them); the handler encrypts before persisting and never echoes
 * them back.
 */
const CreateExternalApiDefinitionInputSchema = z
  .object({
    name: z.string().min(1).max(255),
    description: z.string().max(2000).optional(),
    isActive: z.boolean().optional(),
    method: z.enum(EXTERNAL_HTTP_METHODS),
    baseUrl: z.string().url(),
    pathTemplate: z.string().max(2048).optional(),
    paramSchema: z.array(externalApiParamSchema).max(64),
    responseSchema: z.array(externalApiResponseFieldSchema).max(64),
    responseBodySchemaDefinitionId: z.string().uuid().optional(),
    responseBodySchemaDefinitionVersionId: z.string().uuid().optional(),
    requestBodySchemaDefinitionId: z.string().uuid().optional(),
    requestBodySchemaDefinitionVersionId: z.string().uuid().optional(),
    authType: z.enum(EXTERNAL_API_AUTH_TYPES),
    authConfig: externalApiAuthConfigSchema,
    timeoutMs: z.number().int().min(100).max(600_000).optional(),
    retryConfig: externalApiRetryConfigSchema.optional(),
  })
  .superRefine((val, ctx) => {
    const hasRespD = val.responseBodySchemaDefinitionId !== undefined;
    const hasRespV = val.responseBodySchemaDefinitionVersionId !== undefined;
    if (hasRespD !== hasRespV) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message:
          "Set both responseBodySchemaDefinitionId and responseBodySchemaDefinitionVersionId, or omit both.",
        path: hasRespD
          ? ["responseBodySchemaDefinitionVersionId"]
          : ["responseBodySchemaDefinitionId"],
      });
    }

    const hasReqD = val.requestBodySchemaDefinitionId !== undefined;
    const hasReqV = val.requestBodySchemaDefinitionVersionId !== undefined;
    if (hasReqD !== hasReqV) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message:
          "Set both requestBodySchemaDefinitionId and requestBodySchemaDefinitionVersionId, or omit both.",
        path: hasReqD
          ? ["requestBodySchemaDefinitionVersionId"]
          : ["requestBodySchemaDefinitionId"],
      });
    }

    const hasRespPins =
      typeof val.responseBodySchemaDefinitionId === "string" &&
      val.responseBodySchemaDefinitionId.length > 0 &&
      typeof val.responseBodySchemaDefinitionVersionId === "string" &&
      val.responseBodySchemaDefinitionVersionId.length > 0;
    const hasRespManual = val.responseSchema.length >= 1;
    if (!hasRespPins && !hasRespManual) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message:
          "Describe the API response: pin a response JSON schema, or add at least one manual response field mapping.",
        path: ["responseSchema"],
      });
    }

    const hasReqPins =
      typeof val.requestBodySchemaDefinitionId === "string" &&
      val.requestBodySchemaDefinitionId.length > 0 &&
      typeof val.requestBodySchemaDefinitionVersionId === "string" &&
      val.requestBodySchemaDefinitionVersionId.length > 0;
    const bodyParamCount = val.paramSchema.filter(
      (p) => p.in === "body",
    ).length;

    if (hasReqPins && bodyParamCount > 0) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message:
          "Remove body parameters from paramSchema when using a request JSON schema pin (the schema drives the JSON body).",
        path: ["paramSchema"],
      });
    }

    if (val.method === "GET") {
      if (hasReqPins) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: "GET APIs cannot pin a request JSON body schema.",
          path: ["requestBodySchemaDefinitionId"],
        });
      }
      if (bodyParamCount > 0) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: "GET APIs cannot declare body parameters.",
          path: ["paramSchema"],
        });
      }
    }

    if (
      val.method === "POST" ||
      val.method === "PUT" ||
      val.method === "PATCH"
    ) {
      if (!hasReqPins && bodyParamCount === 0) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message:
            "POST/PUT/PATCH APIs must declare the JSON request body: pin a request JSON schema, or add at least one body parameter in paramSchema.",
          path: ["requestBodySchemaDefinitionId"],
        });
      }
    }

    if (val.method === "DELETE") {
      if (hasReqPins && bodyParamCount > 0) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message:
            "Use either a request JSON schema pin or body parameters on paramSchema, not both.",
          path: ["paramSchema"],
        });
      }
    }
  });

const UpdateExternalApiDefinitionInputSchema =
  CreateExternalApiDefinitionInputSchema.safeExtend({
    id: z.uuid(),
    /**
     * When true, the caller intentionally left `authConfig` blank to
     * preserve the existing encrypted credentials (same UX as Fusion's
     * password-omit pattern). The server keeps the existing blob.
     */
    preserveExistingAuth: z.boolean().optional(),
  });

const DeleteExternalApiDefinitionInputSchema = z.object({
  id: z.uuid(),
});

const ToggleActiveInputSchema = z.object({
  id: z.uuid(),
  isActive: z.boolean(),
});

/**
 * `testRequest` accepts either a saved definition id (so the test runs
 * the persisted/encrypted creds) OR an inline definition (so the user can
 * try a config before saving).
 */
const TestRequestInputSchema = z.discriminatedUnion("source", [
  z.object({
    source: z.literal("registered"),
    apiDefinitionId: z.uuid(),
    paramValues: z.record(z.string(), z.unknown()).prefault({}),
  }),
  z.object({
    source: z.literal("inline"),
    definition: CreateExternalApiDefinitionInputSchema,
    paramValues: z.record(z.string(), z.unknown()).prefault({}),
  }),
]);

const TestRequestResponseSchema = z.object({
  ok: z.boolean(),
  status: z.number().int(),
  /** Sanitized response headers as a flat record. */
  responseHeaders: z.record(z.string(), z.string()),
  /** Parsed response body — JSON object or string. */
  responseBody: z.unknown(),
  /** Raw body string (truncated when large). */
  rawBody: z.string(),
  /** Output sockets: manual JSONPath mappings and/or schema-typed root fields. */
  parsedOutputs: z.record(z.string(), z.unknown()),
  /** Final URL after templating; safe to display. */
  finalUrl: z.string(),
  /** Headers actually sent, with credentials redacted. */
  redactedRequestHeaders: z.record(z.string(), z.string()),
  attempts: z.number().int(),
  durationMs: z.number(),
  error: z.string().optional(),
});

export type TestExternalApiRequest = z.infer<typeof TestRequestInputSchema>;
export type TestExternalApiResponse = z.infer<typeof TestRequestResponseSchema>;
export type CreateExternalApiDefinitionInput = z.infer<
  typeof CreateExternalApiDefinitionInputSchema
>;
export type UpdateExternalApiDefinitionInput = z.infer<
  typeof UpdateExternalApiDefinitionInputSchema
>;

export const externalApisContracts = {
  list: oc
    .input(ListExternalApiDefinitionsRequestSchema)
    .output(ListExternalApiDefinitionsResponseSchema)
    .route({ method: "GET" }),

  get: oc
    .input(GetExternalApiDefinitionRequestSchema)
    .output(ExternalApiDefinitionDTOSchema)
    .route({ method: "GET" }),

  create: oc
    .input(CreateExternalApiDefinitionInputSchema)
    .output(ExternalApiDefinitionDTOSchema)
    .route({ method: "POST" }),

  update: oc
    .input(UpdateExternalApiDefinitionInputSchema)
    .output(ExternalApiDefinitionDTOSchema)
    .route({ method: "POST" }),

  delete: oc
    .input(DeleteExternalApiDefinitionInputSchema)
    .output(z.object({ success: z.literal(true) }))
    .route({ method: "POST" }),

  toggleActive: oc
    .input(ToggleActiveInputSchema)
    .output(ExternalApiDefinitionDTOSchema)
    .route({ method: "POST" }),

  testRequest: oc
    .input(TestRequestInputSchema)
    .output(TestRequestResponseSchema)
    .route({ method: "POST" }),
} as const;

export type ExternalApisContracts = typeof externalApisContracts;

// Re-export the value-types so consumers don't need to import from
// `@erp/workflows` to discriminate on `authType`/`paramType`/etc.
export {
  EXTERNAL_API_AUTH_TYPES,
  EXTERNAL_API_PARAM_LOCATIONS,
  EXTERNAL_API_PARAM_TYPES,
  EXTERNAL_API_RESPONSE_TYPES,
  EXTERNAL_HTTP_METHODS,
};
